import PropTypes from "prop-types";
import React, { useReducer } from "react";
import moment from "moment";
import { uniqBy, sortBy, min, max, last } from "lodash";

import API from "../../API";
import { availableStates } from "../../constants";
import { geocodingResponse } from "../../validation/schemas";
import { compareTwoStateData } from "../../services/misc.service";
import { StringUtils, Parsers } from "../../util";
import { Hazard, Fault, Warning, SystemCheck } from "../../services/event.service";
import {
  POPULATE_FLEETS,
  SET_ACTIVE_FLEET,
  SET_ACTIVE_VEHICLE,
  SET_EVENTS_MAP,
  SET_FLEET_VEHICLES,
  SET_FLEET_VEHICLES_WITH_COMPONENTS,
  SET_GEOFENCES,
  SET_TRIPS,
  SET_ACTIVE_SIM_CONFIG,
  SET_MIN_TIMELINE_DATE,
  SET_MAX_TIMELINE_DATE,
  SET_VEHICLE_TIMELINE_STATUSES,
  PATCH_ACTIVE_VEHICLE,
} from "../types";
import FleetContext from "./fleetContext";
import fleetReducer from "./fleetReducer";

const FleetState = ({ children }) => {
  const initialState = {
    fleets: [],
    // trips keys can be GUIDs for vehicles or trips
    trips: new Map(),
    fleetVehicles: {},
    fleetVehiclesWithComponents: {},
    activeFleet: null,
    activeVehicle: null,
    activeSIMConfig: null,
    geofences: null,
    eventsMap: {
      fault: {
        sc: {},
        md: {},
        bm: {},
      },
      hazard: {
        sc: {},
        md: {},
        bm: {},
      },
      warning: {
        sc: {},
        md: {},
        bm: {},
      },
      notice: {
        sc: {},
        md: {},
        bm: {},
      },
      debug: {
        sc: {},
        md: {},
        bm: {},
      },
      minTimelineDate: moment().utc().subtract(2, "weeks").format("YYYY-MM-DD"),
      maxTimelineDate: moment().utc().format("YYYY-MM-DD"),
      info: { vehicle: ["Self check"] },
      timelineGraphic: { vehicle: ["All systems normal"] },
      tripStart: {
        title: "Vehicles.Detail.History.EventType.TripStart",
        description: "Vehicles.Detail.History.EventType.TripStartDesc",
      },
      tripEnd: {
        title: "Vehicles.Detail.History.EventType.TripEnd",
        description: "Vehicles.Detail.History.EventType.TripEndDesc",
      },
      vehicleTimelineStatuses: [],
    },
  };

  const [state, dispatch] = useReducer(fleetReducer, initialState);

  const parseTrip = (trip) => ({
    ...trip,
    start: Parsers.date(trip.start),
    stop: Parsers.date(trip.stop),
  });

  const fetchFleets = async () => {
    if (!state.fleets.length) {
      try {
        const data = await API.fleets.list();
        const fleets = data
          .filter((f) => !f.inactive)
          .map((f) => ({ ...f, uuid4: StringUtils.uuidFromUrl(f.url) }));
        fleets.sort((f1, f2) => (f1?.name.toLowerCase() >= f2?.name.toLowerCase() ? 1 : -1));
        dispatch({
          type: POPULATE_FLEETS,
          payload: fleets,
        });
        if (!state.activeFleet) {
          const lastFleet = JSON.parse(localStorage.getItem("activeFleetUUID4"));
          const storedFleet = fleets.find((f) =>
            !lastFleet && window?.REACT_APP_ENVIRONMENT === "staging"
              ? f.name.toLowerCase() === "cambridge"
              : f.uuid4 === lastFleet,
          );
          dispatch({
            type: SET_ACTIVE_FLEET,
            payload: storedFleet || fleets[0],
          });
        }
      } catch (err) {
        console.error(err);
      }
    }
  };

  const setActiveFleet = (uuid4) => {
    const fleet =
      uuid4 && state.fleets
        ? state.fleets.find((f) => f?.uuid4.toLowerCase() === uuid4.toLowerCase())
        : null;
    dispatch({
      type: SET_ACTIVE_FLEET,
      payload: fleet,
    });
  };

  const fetchEventsMap = async (vehicleWithComponents) => {
    try {
      // create event map template
      const eventsMap = {
        fault: {
          sc: {},
          md: {},
          bm: {},
        },
        hazard: {
          sc: {},
          md: {},
          bm: {},
        },
        warning: {
          sc: {},
          md: {},
          bm: {},
        },
        notice: {
          sc: {},
          md: {},
          bm: {},
        },
        debug: {
          sc: {},
          md: {},
          bm: {},
        },
        info: { vehicle: ["Self check"] },
        timelineGraphic: { vehicle: ["All systems normal"] },
        tripStart: {
          title: "Vehicles.Detail.History.EventType.TripStart",
          description: "Vehicles.Detail.History.EventType.TripStartDesc",
        },
        tripEnd: {
          title: "Vehicles.Detail.History.EventType.TripEnd",
          description: "Vehicles.Detail.History.EventType.TripEndDesc",
        },
      };

      const vehicleSCVersion = findComponentFirmwareVersion(
        vehicleWithComponents,
        "systemcontroller",
      );
      const vehicleMDVersion = findComponentFirmwareVersion(
        vehicleWithComponents,
        "motorcontroller",
      );
      const vehicleBMSVersion = findComponentFirmwareVersion(vehicleWithComponents, "battery");

      const params = {
        sc_version: vehicleSCVersion,
        mc_version: vehicleMDVersion,
        bm_version: vehicleBMSVersion,
        page_size: 500,
      };

      const data = await API.fleets.getFirmwareEvents(params);

      // fill event map objects with each event from response
      for (let i = 0; i < data.length; i++) {
        const event = data[i];
        eventsMap[event.event_type][event.component][event.code] = event.event_name;
      }

      dispatch({
        type: SET_EVENTS_MAP,
        payload: eventsMap,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const setActiveVehicle = (vehicle) => {
    dispatch({
      type: SET_ACTIVE_VEHICLE,
      payload: vehicle,
    });
  };

  const patchVehicleStatus = async (vehicleId, patchData) => {
    dispatch({
      type: PATCH_ACTIVE_VEHICLE,
      payload: patchData,
    });
    await API.vehicle.patch(vehicleId, patchData);
    dispatch({
      type: PATCH_ACTIVE_VEHICLE,
      payload: patchData,
    });
  };

  const storeTrips = (id, data) => {
    const oldTrips = state.trips.get(id);
    if (!oldTrips || data.length > oldTrips.length || !compareTwoStateData(oldTrips[0], data[0])) {
      dispatch({
        type: SET_TRIPS,
        payload: state.trips.set(id, data),
      });
    }
  };

  const fetchVehiclesWithComponentsByFleetId = async (
    fleetId = state.activeFleet ? state.activeFleet.id : null,
    pageSize = 50,
    page = 1,
  ) => {
    if (!fleetId) return;
    const fleetVehiclesWithComponents = { ...state.fleetVehiclesWithComponents };
    try {
      const data = await API.vehicle.list({
        fleets: fleetId,
        components: true,
        page_size: pageSize,
        page,
      });
      const vehicles = data.results;
      fleetVehiclesWithComponents[fleetId] = {
        vehicles,
        updated: new Date(),
        count: data.count,
      };
      dispatch({
        type: SET_FLEET_VEHICLES_WITH_COMPONENTS,
        payload: fleetVehiclesWithComponents,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchVehiclesInFleet = async (
    fleetId = state.activeFleet ? state.activeFleet.id : null,
  ) => {
    if (!fleetId) return;
    const fleetVehicles = { ...state.fleetVehicles };
    const vehicles = {};
    Object.values(availableStates).forEach((status) => {
      vehicles[status] = [];
    });
    try {
      const data = await API.fleets.listVehicles(fleetId);
      for (let i = 0; i < data.length; i++) {
        const vehicle = data[i];
        if (vehicle.has_state_mismatch) {
          vehicles.mismatched.push(vehicle);
        } else if (vehicle.target_state && vehicles[vehicle.target_state]) {
          vehicles[vehicle.target_state].push(vehicle);
        }
      }
      fleetVehicles[fleetId] = {
        vehicles,
        updated: new Date(),
        count: data.length,
      };
      dispatch({
        type: SET_FLEET_VEHICLES,
        payload: fleetVehicles,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchActiveVehicle = async (vehicleId) => {
    if (!vehicleId) return;
    try {
      const vehicle = await API.vehicle.get(vehicleId, { components: true });
      vehicle.last_updated = Date.now();
      dispatch({
        type: SET_ACTIVE_VEHICLE,
        payload: vehicle,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchActiveSIMConfig = async (vehicleId) => {
    if (!vehicleId) return;
    try {
      const resp = await API.vehicle.simConfig(vehicleId);
      dispatch({
        type: SET_ACTIVE_SIM_CONFIG,
        payload: resp,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTrips = async (idArray) => {
    if (!idArray || !idArray.length) return;
    const promises = idArray.map((tripId) => API.trips.get(tripId));
    try {
      const trips = await Promise.all(promises);
      const clone = new Map(state.trips);
      trips.forEach((trip) => {
        clone.set(trip.id, parseTrip(trip));
      });
      dispatch({
        type: SET_TRIPS,
        payload: clone,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const endTrip = async (vehicleId) => {
    try {
      await API.vehicle.endTrip(vehicleId);
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * Patch the vehicle with an empty object as json data
   * On the backend, this will run state machines without introducing changes
   */
  const emptyPatchVehicle = async (vehicleId) => {
    try {
      await API.vehicle.patch(vehicleId, {});
    } catch (err) {
      console.error(err);
    }
  };

  const findComponentFirmwareVersion = (vehicle, componentString) => {
    if (!vehicle || !vehicle.components) return null;
    const component = vehicle.components.find((obj) => obj.component_type === componentString);
    return component && component.firmware_version ? component.firmware_version : null;
  };

  const findBatteryCharge = (vehicle) => {
    if (!vehicle || !vehicle.components) return null;
    const battery = vehicle.components.find((obj) => obj.component_type === "battery");
    return battery && battery.charge ? battery.charge : null;
  };

  const matchesFirmwarePackage = (vehicleWithComponents, targetFWP) => {
    const vehicleSCVersion = findComponentFirmwareVersion(
      vehicleWithComponents,
      "systemcontroller",
    );
    const vehicleMDVersion = findComponentFirmwareVersion(vehicleWithComponents, "motorcontroller");
    const vehicleBMSVersion = findComponentFirmwareVersion(vehicleWithComponents, "battery");
    const packageSCFW = targetFWP.firmware.find(
      (fw) => fw.component_type === "SystemController" && fw.binary_type === "application",
    );
    const packageMDFW = targetFWP.firmware.find(
      (fw) => fw.component_type === "MotorController" && fw.binary_type === "application",
    );
    const packageBMSFW = targetFWP.firmware.find(
      (fw) => fw.component_type === "Battery" && fw.binary_type === "application",
    );
    return (
      vehicleSCVersion === packageSCFW?.version &&
      vehicleMDVersion === packageMDFW?.version &&
      vehicleBMSVersion === packageBMSFW?.version
    );
  };

  const parseCoordinates = (coords) => {
    if (!coords) return null;

    if (typeof coords[0] === "number") {
      return [coords[1], coords[0]];
    }
    return coords.map((c) => parseCoordinates(c));
  };

  const fetchGeofences = async (fleetId) => {
    try {
      const fences = await API.geofences.listAll({
        fleet: fleetId,
        hide_sidewalks: true,
      });

      const parsedFences = fences.map((f) => ({
        ...f,
        fleetId,
        rules: f.rules.filter((r) => r.active && r.is_operational_time),
        coordinates: parseCoordinates(f?.polygon?.geometry?.coordinates),
      }));

      dispatch({
        type: SET_GEOFENCES,
        payload: parsedFences,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const swapSerializedComponent = API.registration.swapSerializedComponent;
  const swapSim = API.registration.swapSim;
  const removeSerializedComponent = API.registration.removeSerializedComponent;
  const removeSim = API.registration.removeSim;

  const fetchGeocoding = async (city, st, country) => {
    const locationStr = st ? `${city}, ${st}, ${country}` : `${city}, ${country}`;
    const geoData = await API.geocoding.forward(locationStr, { language: "en" });
    if (!geoData.length) throw new Error("Could not retrieve geocoding");
    const geoPlaceData = geoData.find(placeData => placeData.components._category === "place");
    await geocodingResponse.validate(geoPlaceData);
    return geoPlaceData;
  };

  const _pointsToPolygon = (pt1, pt2) => [
    [
      [pt2.lng, pt2.lat],
      [pt1.lng, pt2.lat],
      [pt1.lng, pt1.lat],
      [pt2.lng, pt1.lat],
      [pt2.lng, pt2.lat],
    ],
  ];

  const createNewFleet = async (data) => {
    /*
     * This function will check for existing fleet/market/rate_plan
     * before creating them anew. If an existing object is found, new
     * geocoding data will be patched to update the object. No other data
     * will be changed.
     */

    // get and parse geocoded data
    const geoData = await fetchGeocoding(data.city, data.state, data.country);
    const whichName = [
      geoData.components.city,
      geoData.components.town,
      geoData.components.village,
      geoData.components.municipality,
      geoData.components.county,
    ].find((n) => !!n);
    const marketName = whichName.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const marketCountry = geoData.components["ISO_3166-1_alpha-2"];
    const boundingBox = {
      type: "Polygon",
      coordinates: _pointsToPolygon(geoData.bounds.northeast, geoData.bounds.southwest),
    };
    const canonicalName = `${marketCountry}_${
      marketCountry === "US" ? `${geoData.components.state_code}_` : ""
    }${marketName.replace(/[^\w\s]|_/g, "").replace(/\s+/g, "_")}`.toLowerCase();

    // check for existing fleet
    const fleets = await API.fleets.list({ canonical_name: canonicalName, inactive: false });

    let ratePlan = {};
    if (!(fleets[0]?.rate_plan_id && fleets[0]?.external_rate_plan)) {
      // create new billing ratePlan
      const ratePlanData = {
        currency: geoData.annotations.currency.iso_code,
        auto_renew_options: [
          {
            amount: 500,
            bonus: 0,
          },
          {
            amount: 1500,
            bonus: 200,
          },
          {
            amount: 3000,
            bonus: 500,
          },
        ],
        tax: {
          name: "Sales Tax",
          rate: 0.0,
          type: "inclusive",
        },
        payment_providers: [
          { default: true, provider: "stripe" },
          { default: false, provider: "braintree" },
        ],
      };
      ratePlan = await API.billing.createRatePlan(ratePlanData);
      ratePlan.id = StringUtils.uuidFromUrl(ratePlan.url);
    } else {
      // use existing ratePlan
      ratePlan.id = fleets[0].rate_plan_id;
      ratePlan.url = fleets[0].external_rate_plan;
    }

    // check for existing market
    let market = null;
    const marketData = {
      name: marketName,
      country: marketCountry,
      bounding_box: boundingBox,
    };
    const markets = await API.markets.list({ name: marketName, country: marketCountry });
    if (!markets.length) {
      // create new market
      market = await API.markets.create(marketData);
    } else {
      market = await API.markets.patch(StringUtils.uuidFromUrl(markets[0].url), marketData);
    }

    // create/patch fleet
    const fleetData = {
      market: market.url,
      name: fleets[0]?.name || market.name,
      canonical_name: fleets[0]?.canonical_name || canonicalName,
      abbreviation: data.abbreviation,
      bounding_box: market.bounding_box,
      center_point: { type: "Point", coordinates: [geoData.geometry.lng, geoData.geometry.lat] },
      timezone: geoData.annotations.timezone.name,
      local_speed_label: geoData.annotations.roadinfo.speed_in,
      default_firmware_package:
        fleets[0]?.default_firmware_package || window.REACT_APP_DEFAULT_FIRMWARE_PACKAGE,
      external_rate_plan: ratePlan.url,
      rate_plan_id: ratePlan.id,
      parallelize_start_trip: false,
      ...(marketCountry !== "US"
        ? {}
        : { state: fleets[0]?.state || geoData.components.state_code }),
    };
    let fleet = null;
    if (!fleets.length) {
      // create new fleet
      fleet = await API.fleets.create(fleetData);
    } else {
      fleetData.rate_plan_id = fleets[0].rate_plan_id;
      fleetData.external_rate_plan = fleets[0].external_rate_plan;
      fleet = await API.fleets.patch(StringUtils.uuidFromUrl(fleets[0].url), fleetData);
    }
    fleet.uuid4 = StringUtils.uuidFromUrl(fleet.url);

    const newFleets = state.fleets.filter((f) => f.uuid4 !== fleet.uuid4);
    // add fleet to state
    dispatch({
      type: POPULATE_FLEETS,
      payload: [...newFleets, fleet].sort((f1, f2) =>
        f1?.name.toLowerCase() >= f2?.name.toLowerCase() ? 1 : -1,
      ),
    });

    // return fleet id string
    return fleet.uuid4;
  };

  const updateActiveFleet = async (data) => {
    if (!state.activeFleet?.uuid4) return;
    if (data.operating_hours_begin)
      data.operating_hours_begin = StringUtils.convertTz(
        data.operating_hours_begin,
        state.activeFleet.timezone,
        "UTC",
        "TIME_24_SIMPLE",
      );
    if (data.operating_hours_soft_end)
      data.operating_hours_soft_end = StringUtils.convertTz(
        data.operating_hours_soft_end,
        state.activeFleet.timezone,
        "UTC",
        "TIME_24_SIMPLE",
      );
    try {
      const fleet = await API.fleets.patch(state.activeFleet.uuid4, data);
      fleet.uuid4 = StringUtils.uuidFromUrl(fleet.url);
      const newFleets = state.fleets.filter((f) => f.uuid4 !== fleet.uuid4);
      dispatch({
        type: POPULATE_FLEETS,
        payload: [...newFleets, fleet].sort((f1, f2) =>
          f1?.name.toLowerCase() >= f2?.name.toLowerCase() ? 1 : -1,
        ),
      });
      dispatch({
        type: SET_ACTIVE_FLEET,
        payload: fleet,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const sanitizeEvent = (event) => {
    event.timestamp = moment.utc(event.timestamp).format();
    switch (event.type) {
      case "fault":
        return new Fault(state.eventsMap.fault, event).toRepresentation();
      case "hazard":
        return new Hazard(state.eventsMap.hazard, event).toRepresentation();
      case "warning":
        return new Warning(state.eventsMap.warning, event).toRepresentation();
      default:
        return new SystemCheck(state.eventsMap.fault, event).toRepresentation();
    }
  };

  const setMinTimelineDate = (date) => {
    dispatch({
      type: SET_MIN_TIMELINE_DATE,
      payload: date,
    });
  };

  const setMaxTimelineDate = (date) => {
    dispatch({
      type: SET_MAX_TIMELINE_DATE,
      payload: date,
    });
  };

  let fetchTimelineAbortController = new AbortController();

  const fetchVehicleTimeline = async (vehicleId) => {
    fetchTimelineAbortController.abort();
    fetchTimelineAbortController = new AbortController();

    const fetchTimeline = async () => {
      const abortController = fetchTimelineAbortController;
      try {
        let endDate = state.maxTimelineDate;
        let statuses = await API.vehicle.timeline(vehicleId, {
          start_date: state.minTimelineDate,
          end_date: endDate,
        });

        for (
          let i = 0;
          i < (window.REACT_APP_MAX_TIMELINE_REQUESTS || 20) && !abortController.signal.aborted;
          i++
        ) {
          // eslint-disable-next-line no-await-in-loop
          const newStatuses = await API.vehicle.timeline(
            vehicleId,
            {
              start_date: state.minTimelineDate,
              end_date: endDate,
            },
            abortController.signal,
          );

          if (newStatuses.length === 0 || newStatuses[newStatuses.length - 1].timestamp === endDate)
            break;

          statuses = uniqBy(
            statuses.concat(newStatuses),
            (status) => `${status.id}-${status.timestamp}`,
          );
          endDate = statuses[statuses.length - 1].timestamp;

          if (i === 0 && !statuses[0].end_time) statuses[0].end_time = state.maxTimelineDate;
        }

        // NOTE: statuses are in descending order by timestamp, so "previous" statuses are
        // more recent
        const reducedStatuses = statuses.reduce((acc, currentStatus) => {
          // stitch together statuses with same vehicle_state, since timeline only cares about
          // changes in status (except trips), not every single status
          const prevStatus = last(acc);

          if (
            prevStatus &&
            prevStatus.vehicle_state === currentStatus.vehicle_state &&
            !(prevStatus.is_trip && currentStatus.is_trip)
          ) {
            acc.pop();
            acc.push({
              ...prevStatus,
              ...currentStatus,
              timeline_events: uniqBy(
                sortBy(
                  [...prevStatus.timeline_events, ...currentStatus.timeline_events],
                  "timestamp",
                ),
                (event) => `${event.id}-${event.timestamp}`,
              ),
              end_time: max([prevStatus.end_time, currentStatus.end_time]),
              start_time: min([prevStatus.end_time, currentStatus.end_time]),
              timestamp: min([prevStatus.timestamp, currentStatus.timestamp]),
            });
          } else {
            acc.push(currentStatus);
          }

          return acc;
        }, []);

        return reducedStatuses;
      } catch (err) {
        console.error(err);
        return null;
      }
    };

    const statuses = await fetchTimeline();

    if (statuses)
      dispatch({
        type: SET_VEHICLE_TIMELINE_STATUSES,
        payload: statuses,
      });
  };

  const setActiveVehicleState = async (vehicleId, status) => {
    await API.vehicle.setState(vehicleId, status);
  };

  return (
    <FleetContext.Provider
      value={{
        fleets: state.fleets,
        trips: state.trips,
        fleetVehicles: state.fleetVehicles,
        fleetVehiclesWithComponents: state.fleetVehiclesWithComponents,
        activeFleet: state.activeFleet,
        activeVehicle: state.activeVehicle,
        activeSIMConfig: state.activeSIMConfig,
        geofences: state.geofences,
        registrationFails: state.registrationFails,
        eventsMap: state.eventsMap,
        minTimelineDate: state.minTimelineDate,
        maxTimelineDate: state.maxTimelineDate,
        vehicleTimelineStatuses: state.vehicleTimelineStatuses,
        fetchFleets,
        setActiveFleet,
        setActiveVehicle,
        fetchActiveSIMConfig,
        storeTrips,
        fetchVehiclesInFleet,
        fetchVehiclesWithComponentsByFleetId,
        fetchActiveVehicle,
        fetchTrips,
        findBatteryCharge,
        endTrip,
        emptyPatchVehicle,
        findComponentFirmwareVersion,
        fetchGeofences,
        matchesFirmwarePackage,
        swapSerializedComponent,
        swapSim,
        removeSerializedComponent,
        removeSim,
        createNewFleet,
        updateActiveFleet,
        fetchEventsMap,
        sanitizeEvent,
        setMaxTimelineDate,
        setMinTimelineDate,
        fetchVehicleTimeline,
        patchVehicleStatus,
        setActiveVehicleState,
      }}
    >
      {children}
    </FleetContext.Provider>
  );
};

FleetState.propTypes = {
  children: PropTypes.node.isRequired,
};

export default FleetState;
